import { CSSProperties, useCallback, useMemo, useRef, useState } from 'react';

import { useJobQuoteContext } from 'components/JobQuote/context';

export interface JobQuoteCollapsibleHook {
  collapseRef: React.MutableRefObject<HTMLDivElement | null>;
  open: boolean;
  onClick: () => void;
  contentStyle: CSSProperties;
  isLoading: boolean;
  disabled: boolean;
  iconClassName: string;
  buttonText: string;
}

export default function useJobQuoteCollapsible(
  milliseconds = 300,
): JobQuoteCollapsibleHook {
  const { isLoading } = useJobQuoteContext();
  const [open, setOpen] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [maxHeight, setMaxHeight] = useState<string>('0px');
  const collapseRef = useRef<HTMLDivElement | null>(null);

  const handleDisable = useCallback(() => {
    setDisabled(true);
    setTimeout(() => setDisabled(false), milliseconds);
  }, [milliseconds]);

  const onClick = useCallback(() => {
    const clientHeight = `${collapseRef.current?.clientHeight || 100}px`;
    if (disabled || isLoading) return;
    handleDisable();
    if (!open) {
      setOpen(true);
      setTimeout(() => {
        setMaxHeight(clientHeight);
      }, 10);
      setTimeout(() => {
        setMaxHeight('none');
      }, milliseconds + 100);
      return;
    }
    setOpen(false);
    setMaxHeight(clientHeight);
    setTimeout(() => setMaxHeight('0px'), 10);
  }, [handleDisable, open, disabled, milliseconds, isLoading]);

  const style = useMemo(
    () => ({
      maxHeight,
      transitionDuration: `${milliseconds}ms`,
    }),
    [maxHeight, milliseconds],
  );

  const iconClassName = useMemo(
    () =>
      ['my-auto transition-all', open ? 'rotate-180' : 'rotate-0'].join(' '),
    [open],
  );

  const buttonText = useMemo(
    () => (open ? 'Hide details' : 'Show details'),
    [open],
  );

  return {
    collapseRef,
    open,
    onClick,
    disabled,
    isLoading,
    contentStyle: style,
    iconClassName,
    buttonText,
  };
}
